import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { canonicalFilters, queryKey, suggestSearchName } from '../filters.js';

export const MAX_SAVED_FILTERS = 20; // matches MAX_SAVED_SEARCHES on the server
export const isSameFilters = (a, b) => queryKey(canonicalFilters(a || {})) === queryKey(canonicalFilters(b || {}));

/**
 * Saved Filters (DECISIONS #23) — the member's saved filter combinations
 * (the same records as "Save This Search" and the toolbar shortcuts).
 * Tap a name to apply it; rename it; replace it with the filters now in
 * view ("Update"); or delete it. "Save current filters" adds a new one.
 */
export function SavedFiltersLocked({ count, onLocked }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onLocked}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLocked?.();
        }
      }}
      data-testid="sr-saved-filters-locked"
      className="flex w-full cursor-pointer items-start gap-[10px] rounded-[10px] border border-sr-gold/45 bg-sr-gold/[.06] px-[12px] py-[10px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
    >
      <Lock size={18} className="mt-[2px] shrink-0 text-sr-gold" aria-hidden />
      <span className="min-w-0">
        <span className="inline-block rounded-[5px] bg-sr-gold px-[6px] py-[1px] text-[10.5px] font-bold text-sr-bg">Premiere Only</span>
        <span className="mt-[5px] block text-[12.5px] font-semibold leading-snug text-[#f3dc9a]">
          Save the filters you use most and reapply them in one tap with a{' '}
          <Link
            to="/app/upgrade"
            onClick={(e) => e.stopPropagation()}
            data-testid="sr-saved-filters-upgrade-link"
            className="font-extrabold text-sr-gold underline underline-offset-2 hover:text-[#ffd75e]"
          >
            Premiere membership
          </Link>
          .
        </span>
        {count > 0 && (
          <span className="mt-[4px] block text-[12px] text-sr-muted">
            Your {count} saved filter{count === 1 ? ' is' : 's are'} kept and will be back when you rejoin Premiere.
          </span>
        )}
      </span>
    </div>
  );
}

export function SavedFiltersSection({ saved, genres, providers, languages, hasFilters, onLocked }) {
  const { locked = false, list = [], current, suggestedName, canSave, save, apply, update, remove, lastAppliedId } = saved;
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [adding, setAdding] = useState(null); // name being typed, or null
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong — please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };
  const atLimit = list.length >= MAX_SAVED_FILTERS;
  if (locked) return <SavedFiltersLocked count={list.length} onLocked={onLocked} />;
  const field =
    'h-[30px] min-w-0 flex-1 rounded-[7px] border border-sr-field-line bg-sr-field px-[9px] text-[13px] text-sr-text placeholder:text-sr-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold';
  const smallBtn =
    'rounded-[7px] px-[9px] py-[4px] text-[12.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold disabled:opacity-50';
  const iconBtn =
    'grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[7px] text-sr-muted hover:bg-white/5 hover:text-sr-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold';

  return (
    <div data-testid="sr-saved-filters" className="space-y-[8px]">
      {list.length === 0 && (
        <p data-testid="sr-saved-filters-empty" className="m-0 text-[12.5px] text-sr-muted">
          Save the filters you use often, then reapply them here in one tap.
        </p>
      )}
      {list.length > 0 && (
        <ul className="m-0 list-none space-y-[6px] p-0">
          {list.map((s) => {
            const inUse = isSameFilters(s.filters, current);
            const changed = lastAppliedId === s.id && !inUse && hasFilters;
            if (renaming?.id === s.id) {
              return (
                <li key={s.id} className="rounded-[9px] border border-sr-tile-line p-[8px]">
                  <form
                    className="flex items-center gap-[6px]"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!renaming.name.trim()) return;
                      if (await run(() => update(s, { name: renaming.name.trim() }))) setRenaming(null);
                    }}
                  >
                    <input autoFocus aria-label="New name" maxLength={80} value={renaming.name} onChange={(e) => setRenaming({ id: s.id, name: e.target.value })} className={field} data-testid={`sr-saved-rename-input-${s.id}`} />
                    <button type="submit" disabled={busy} className={`${smallBtn} bg-sr-gold text-sr-bg`}>Save</button>
                    <button type="button" onClick={() => setRenaming(null)} className={`${smallBtn} text-sr-soft`}>Cancel</button>
                  </form>
                </li>
              );
            }
            if (confirmDelete === s.id) {
              return (
                <li key={s.id} className="flex items-center gap-[6px] rounded-[9px] border border-red-400/40 p-[8px] text-[13px] text-sr-text">
                  <span className="min-w-0 flex-1 truncate">Delete “{s.name}”?</span>
                  <button
                    type="button"
                    disabled={busy}
                    data-testid={`sr-saved-delete-confirm-${s.id}`}
                    onClick={async () => {
                      if (await run(() => remove(s))) {
                        setConfirmDelete(null);
                      }
                    }}
                    className={`${smallBtn} bg-red-500/90 text-white`}
                  >
                    Delete
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(null)} className={`${smallBtn} text-sr-soft`}>Cancel</button>
                </li>
              );
            }
            return (
              <li
                key={s.id}
                data-testid={`sr-saved-filter-${s.id}`}
                className={`rounded-[9px] border p-[8px] ${inUse ? 'border-sr-gold/70 bg-sr-gold/[.07]' : 'border-sr-tile-line'}`}
              >
                <div className="flex items-start gap-[6px]">
                  <button
                    type="button"
                    onClick={() => apply(s)}
                    aria-label={`Apply saved filter ${s.name}`}
                    data-testid={`sr-saved-apply-${s.id}`}
                    className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
                  >
                    <span className="flex items-center gap-[6px]">
                      <span className="truncate text-[13.5px] font-semibold text-sr-text">{s.name}</span>
                      {inUse && <span className="shrink-0 rounded-full bg-sr-gold px-[6px] text-[10.5px] font-bold text-sr-bg">In use</span>}
                    </span>
                    <span className="block truncate text-[11.5px] text-sr-muted">{suggestSearchName(s.filters || {}, genres, providers, languages)}</span>
                  </button>
                  {update && (
                    <button type="button" onClick={() => setRenaming({ id: s.id, name: s.name })} aria-label={`Rename ${s.name}`} title="Rename" className={iconBtn} data-testid={`sr-saved-rename-${s.id}`}>
                      <Pencil size={13} aria-hidden />
                    </button>
                  )}
                  {remove && (
                    <button type="button" onClick={() => setConfirmDelete(s.id)} aria-label={`Delete ${s.name}`} title="Delete" className={iconBtn} data-testid={`sr-saved-delete-${s.id}`}>
                      <Trash2 size={13} aria-hidden />
                    </button>
                  )}
                </div>
                {changed && update && (
                  <div className="mt-[6px] flex items-center justify-between gap-[6px] border-t border-sr-divider pt-[6px]">
                    <span className="text-[12px] text-sr-soft">You&apos;ve changed these filters.</span>
                    <button
                      type="button"
                      disabled={busy}
                      data-testid={`sr-saved-update-${s.id}`}
                      aria-label={`Save the current filters to ${s.name}`}
                      onClick={() => run(() => update(s, { filters: current }))}
                      className={`${smallBtn} flex shrink-0 items-center gap-[4px] whitespace-nowrap border border-sr-gold/70 text-sr-gold hover:bg-sr-gold/10`}
                    >
                      <RefreshCw size={12} aria-hidden /> Save changes
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canSave &&
        (adding !== null ? (
          <form
            className="flex items-center gap-[6px]"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!adding.trim()) return;
              if (await run(() => save(adding.trim()))) setAdding(null);
            }}
          >
            <input autoFocus aria-label="Name for these filters" maxLength={80} value={adding} onChange={(e) => setAdding(e.target.value)} className={field} data-testid="sr-saved-new-input" />
            <button type="submit" disabled={busy} className={`${smallBtn} bg-sr-gold text-sr-bg`}>Save</button>
            <button type="button" onClick={() => setAdding(null)} className={`${smallBtn} text-sr-soft`}>Cancel</button>
          </form>
        ) : (
          <button
            type="button"
            disabled={!hasFilters || atLimit}
            onClick={() => setAdding(suggestedName || '')}
            data-testid="sr-saved-new"
            className="flex w-full items-center justify-center gap-[6px] rounded-[9px] border border-dashed border-sr-field-line py-[7px] text-[13px] font-semibold text-sr-text hover:border-sr-gold hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-sr-field-line disabled:hover:text-sr-text"
          >
            <Plus size={14} aria-hidden /> Save current filters
          </button>
        ))}
      {canSave && !hasFilters && adding === null && <p className="m-0 text-[11.5px] text-sr-muted">Set some filters first, then save them here.</p>}
      {atLimit && <p className="m-0 text-[11.5px] text-sr-muted">You can keep up to {MAX_SAVED_FILTERS} saved filters — delete one to add another.</p>}
      {error && <p role="alert" className="m-0 text-[12px] text-red-300">{error}</p>}
    </div>
  );
}
